import { useEffect, useState } from 'react';

/**
 * RELOADING THE SEARCH PAGE STARTS IT CLEAN.
 *
 * The page seeds itself from its URL — `?subject=` (written back whenever the subject switch
 * moves), and `?q=` / `?name=` / `?plate=` that a link arrived with (the Watch List's Track
 * button). Those were re-applied on every reload, so an operator pressing reload to start over got
 * the last subject and the last linked plate back instead. A reload now clears them; `?mode=` is
 * kept, because Search vs Track is the view the operator is on, not a filter.
 *
 * Only a reload OF THIS PAGE counts. The browser records how the document was loaded; if it was a
 * reload and its URL was this page, the first search mount of that load starts fresh. A link
 * followed inside the app afterwards (another page's Track button) is a normal navigation and
 * keeps its seeds — the decision is made once per document load and then spent.
 */
export const RESET_ON_RELOAD = ['q', 'name', 'plate', 'subject'] as const;

/** null = not decided yet for this document load; false = spent (or not a reload). */
let pending: boolean | null = null;

function reloadOfThisPage(pathname: string): boolean {
  if (pending !== null) return pending;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    pending = !!nav && nav.type === 'reload' && new URL(nav.name).pathname === pathname;
  } catch {
    pending = false;
  }
  return pending;
}

type SetParams = (
  next: (prev: URLSearchParams) => URLSearchParams,
  opts?: { replace?: boolean },
) => void;

/**
 * True on the mount that follows a reload of the search page — the host then passes NO seeds — and
 * strips the seeds from the address bar so the next reload, and a copied link, are clean too.
 */
export function useFreshOnReload(setParams: SetParams): boolean {
  const [fresh] = useState(() => reloadOfThisPage(window.location.pathname));
  useEffect(() => {
    if (!fresh) return;
    pending = false;
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      for (const k of RESET_ON_RELOAD) p.delete(k);
      return p;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh]);
  return fresh;
}
